import { Icon } from '@eventbrite/eds-icon';
import { gettext } from '@eventbrite/i18n';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import { logEvent } from '@eventbrite/statsig';
import { Wrapper } from '@googlemaps/react-wrapper';
import React from 'react';
import { GACodes, Location } from '../../../../types';
import { EVENT_LOCATION_MAP_ID } from '../../../_shared/constants';
import { EventDetailsMainInner } from '../../EventDetails';
import './Map.scss';
import {
    buttonsConfig,
    G_MAPS_LINK,
    mapOptions,
    markerOptions,
} from './MapStyles';

export type MapProps = {
    location: Location;
    mapZoom: number;
    gMapsApiKey: string;
    isMapExpanded: boolean;
};

interface GMapProps extends google.maps.MapOptions {
    style: { [key: string]: string };
}

const getButtonList = (location: Location): JSX.Element[] =>
    buttonsConfig.map((btnConfig, index) => {
        const extraParams = new URLSearchParams(
            btnConfig.queryParams,
        ).toString();
        const link = `${G_MAPS_LINK}&daddr=${location.latitude},${location.longitude}&${extraParams}`;
        const label = btnConfig.label;

        return (
            <li key={index}>
                <a
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    className="direction-button"
                    aria-label={btnConfig.ariaLabel}
                    onClick={() =>
                        trackAnalyticsEvent({
                            category: GACodes.GA_CATEGORY_LISTING,
                            action: GACodes.GA_ACTION_DIRECTIONS,
                            label: label,
                        })
                    }
                >
                    <Icon type={btnConfig.icon} color="ui-blue--hover" />
                </a>
            </li>
        );
    });

const MapButtons = (location: Location): JSX.Element => {
    return (
        <div className="directions__buttons">
            <ul>{getButtonList(location)}</ul>
        </div>
    );
};

const GMap: React.FC<GMapProps> = ({ children, style, ...options }) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [map, setMap] = React.useState<google.maps.Map>();

    React.useEffect(() => {
        if (ref.current && !map) {
            const googleMap = new window.google.maps.Map(ref.current, {});

            googleMap.addListener('tilesloaded', function () {
                logEvent('Listing - Event Map - Render - Map');
            });

            setMap(googleMap);
        }
    }, [ref, map]);

    React.useEffect(() => {
        if (map) {
            map.setOptions(options);
        }
    }, [map, options]);

    React.useEffect(() => {
        if (map) {
            map.addListener('zoom_changed', () => {
                trackAnalyticsEvent({
                    category: GACodes.GA_CATEGORY_LISTING,
                    action: GACodes.GA_ACTION_MAP_INTERACTION,
                    label: GACodes.GA_LABEL_ZOOM,
                });
                logEvent('Listing - Event Map - Change - Zoom');
            });
        }
    }, [map]);

    const cloneChildrenAddingMapProp = () =>
        React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
                return React.cloneElement(child, { map });
            }
        });

    return (
        <>
            <div ref={ref} style={style}></div>
            {cloneChildrenAddingMapProp()}
        </>
    );
};

export const Marker: React.FC<google.maps.MarkerOptions> = (options) => {
    const [marker, setMarker] = React.useState<google.maps.Marker>();

    React.useEffect(() => {
        if (!marker) {
            setMarker(new google.maps.Marker());
        }
    }, [marker]);

    React.useEffect(() => {
        if (marker) {
            marker.setOptions({ ...options });
        }
    }, [marker, options]);

    return null;
};

const MapContainer = ({
    mapZoom,
    location,
    mapOptions,
    markerOptions,
    api_key,
}: {
    mapZoom: number;
    location: Location;
    mapOptions: google.maps.MapOptions;
    markerOptions: google.maps.MarkerOptions;
    api_key: string;
}) => {
    const center: google.maps.LatLngLiteral = {
        lat: location.latitude,
        lng: location.longitude,
    };
    return (
        <Wrapper apiKey={api_key}>
            <GMap
                center={center}
                zoom={mapZoom}
                style={{ width: '100%', height: '376px' }}
                {...mapOptions}
            >
                <Marker
                    position={{
                        lat: location.latitude,
                        lng: location.longitude,
                    }}
                    {...markerOptions}
                />
            </GMap>
        </Wrapper>
    );
};

export const Map: React.FunctionComponent<MapProps> = ({
    location,
    mapZoom,
    gMapsApiKey,
    isMapExpanded,
}) => {
    const classes = isMapExpanded ? '' : 'eds-hide';

    return (
        <>
            <div
                id={EVENT_LOCATION_MAP_ID}
                className={`eds-fx--fade-in ${classes}`}
                role="region"
                data-testid={EVENT_LOCATION_MAP_ID}
                hidden={isMapExpanded ? false : true}
                tabIndex={-1}
            >
                <h3 className="eds-is-hidden-accessible">
                    {gettext('Map and directions')}
                </h3>
                <div className="map-wrapper">
                    <div className="map-wrapper__inner">
                        <MapContainer
                            mapZoom={mapZoom}
                            location={location}
                            mapOptions={mapOptions}
                            markerOptions={markerOptions}
                            api_key={gMapsApiKey}
                        />
                    </div>
                </div>
                <EventDetailsMainInner>
                    <div className="directions">
                        <h4 className="directions__heading">
                            {gettext('How to get there')}
                        </h4>
                        <MapButtons {...location} />
                    </div>
                </EventDetailsMainInner>
            </div>
        </>
    );
};
